import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type Article } from "../lib/api.ts";

export default function PopoutPreview() {
  const { id } = useParams<{ id: string }>();
  const [article, setArticle] = useState<Article | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const articleId = parseInt(id ?? "0", 10);

  useEffect(() => {
    if (!articleId) { setError("Invalid article ID"); setLoading(false); return; }
    api.articles.get(articleId)
      .then(r => setArticle(r.article))
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [articleId]);

  // Close window helper
  function closeWindow() { window.close(); }

  const htmlContent = article?.rewritten_body ?? "";

  // Extract pull quote from body if it has <pullquote> tags
  const pullQuoteMatch = htmlContent.match(/<pullquote>(.*?)<\/pullquote>/i);
  const pullQuoteText = pullQuoteMatch?.[1]?.trim() || article?.pull_quote || "";

  // Strip <pullquote> tags for rendering, add highlight
  const displayBody = htmlContent.replace(/<\/?pullquote>/gi, "");

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-400 text-sm">Loading article…</p>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-500 text-sm mb-3">{error || "Article not found"}</p>
          <button onClick={closeWindow} className="text-brand-500 hover:underline text-sm">Close window</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 font-mono">Article #{article.id}</span>
          <span className="text-xs text-gray-400">{article.feed_name}</span>
          <span className="text-xs text-gray-400">
            Style {article.style_mode?.toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.print()}
            className="btn-ghost btn btn-sm text-xs"
          >
            Print
          </button>
          <button
            onClick={closeWindow}
            className="btn-ghost btn btn-sm text-xs"
          >
            Close
          </button>
        </div>
      </div>

      {/* Article content — full width */}
      <div className="max-w-4xl mx-auto px-8 py-10">
        {/* Headline */}
        <h1 className="text-3xl font-bold text-gray-900 leading-tight mb-4">
          {article.rewritten_title || article.original_title}
        </h1>

        {/* Style A: BLUF subhead */}
        {article.style_mode === "a" && article.bluf_subhead && (
          <p className="text-lg text-gray-600 font-medium mb-6 border-l-4 border-brand-400 pl-4">
            {article.bluf_subhead}
          </p>
        )}

        {/* Style B: Deck */}
        {article.style_mode === "b" && article.deck && (
          <p className="text-lg text-gray-600 italic mb-6">
            {article.deck}
          </p>
        )}

        {/* Pull quote highlight */}
        {pullQuoteText && (
          <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 mb-6">
            <p className="text-xs text-brand-500 uppercase tracking-wider font-medium mb-1">Pull Quote</p>
            <p className="text-lg text-brand-700 italic leading-relaxed">"{pullQuoteText}"</p>
          </div>
        )}

        {/* Body */}
        {displayBody ? (
          <div
            className="prose prose-gray max-w-none
              prose-headings:text-gray-900 prose-headings:font-semibold
              prose-p:text-gray-700 prose-p:leading-relaxed
              prose-strong:text-gray-900
              prose-ul:text-gray-700
              prose-li:text-gray-700
              prose-a:text-brand-500
              [&_em]:text-gray-500 [&_em]:not-italic [&_em]:text-sm
              text-base leading-relaxed"
            dangerouslySetInnerHTML={{ __html: displayBody }}
          />
        ) : (
          <p className="text-gray-400 italic">No rewritten content.</p>
        )}

        {/* Meta footer */}
        {article.rewrite_model && (
          <div className="mt-10 pt-6 border-t border-gray-100">
            <p className="text-xs text-gray-400">
              Rewritten with {article.rewrite_model}
              {article.rewrite_tokens && ` · ${article.rewrite_tokens.toLocaleString()} tokens`}
              {article.rewrite_cost_usd != null && ` · $${article.rewrite_cost_usd.toFixed(4)}`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}